import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb, { type Crumb } from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/base/Pagination";
import { articles } from "@/mocks/news";
import { articlesByCategory, getCategory } from "@/utils/news";

const RANGE_OPTIONS = ["全部", "最新一周", "最新一月"];
const SORT_OPTIONS: { key: "new" | "hot"; label: string }[] = [
  { key: "new", label: "最新发布" },
  { key: "hot", label: "最多热度" },
];
const PAGE_SIZE = 5;

const newestTime = Math.max(
  ...articles.map((a) => new Date(`${a.date}T00:00:00`).getTime()),
);

export default function CategoryPage() {
  const { categoryName = "" } = useParams();
  const isAll = categoryName === "all";
  const category = getCategory(categoryName);

  const [sub, setSub] = useState("全部");
  const [range, setRange] = useState("全部");
  const [sort, setSort] = useState<"new" | "hot">("new");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setSub("全部");
    setRange("全部");
    setSort("new");
    setPage(1);
  }, [categoryName]);

  const source = useMemo(() => {
    if (isAll) return articles;
    if (!category) return [];
    return articlesByCategory(category.slug);
  }, [isAll, category]);

  const filtered = useMemo(() => {
    let list = [...source];
    if (sub !== "全部") list = list.filter((a) => a.subCategory === sub);
    if (range !== "全部") {
      const days = range === "最新一周" ? 7 : 30;
      const limit = newestTime - days * 24 * 60 * 60 * 1000;
      list = list.filter(
        (a) => new Date(`${a.date}T00:00:00`).getTime() >= limit,
      );
    }
    list.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      return a.date < b.date ? 1 : -1;
    });
    return list;
  }, [source, sub, range, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const title = isAll ? "全部爆料" : category ? `${category.name}爆料` : "分类";
  const intro = isAll
    ? "站内全部爆料与深度报道，按最新或热度自由浏览。"
    : category?.intro ?? "该分类暂无内容。";

  const crumbs: Crumb[] = [{ label: "分类" }, { label: title }];

  const resetFilters = () => {
    setSub("全部");
    setRange("全部");
    setSort("new");
    setPage(1);
  };

  return (
    <div className="bg-background-50">
      <Seo
        title={`${title} - 51爆料热点爆料`}
        description={
          isAll
            ? "51爆料全站热点新闻与内幕爆料合集，涵盖社会、娱乐、科技多个领域。"
            : `提供最新的${title}新闻与深度报道，第一时间掌握${title}相关热点与内幕。`
        }
        keywords={`${title},51爆料,热点新闻,内幕爆料,${title}资讯`}
      />

      <div className="bg-primary-500 text-white">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-6 py-8 md:py-10">
          <h1 className="text-2xl md:text-3xl font-heading font-black">
            {title}
          </h1>
          <p className="mt-2 text-sm md:text-base text-white/85 max-w-2xl">
            {intro}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-6 py-6">
        <Breadcrumb items={crumbs} />
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-6 pb-16">
        {/* Filters */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5 mb-6 flex flex-col gap-4">
          {!isAll && category && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-2">子分类</span>
              {["全部", ...category.subs].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setSub(s);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    sub === s
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-2">发布时间</span>
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    setRange(r);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    range === r
                      ? "bg-secondary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              <span className="text-sm text-foreground-500 mr-2">排序</span>
              {SORT_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => {
                    setSort(o.key);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    sort === o.key
                      ? "bg-accent-500 text-foreground-950"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="text-sm text-foreground-500 mb-4">
          共找到 <span className="text-primary-600 font-bold">{filtered.length}</span>{" "}
          条爆料
        </p>

        {pageItems.length > 0 ? (
          <>
            <div className="flex flex-col gap-4">
              {pageItems.map((article) => (
                <ArticleCard key={article.id} article={article} layout="row" />
              ))}
            </div>
            <Pagination
              current={currentPage}
              total={totalPages}
              onChange={setPage}
            />
          </>
        ) : (
          <div className="bg-background-50 border border-dashed border-background-300 rounded-lg py-16 px-6 text-center">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
              <i className="ri-inbox-line" />
            </div>
            <p className="mt-4 text-base font-medium text-foreground-800">
              没有找到符合条件的爆料
            </p>
            <p className="mt-1 text-sm text-foreground-500">
              试试调整筛选条件，或查看更多分类内容。
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选条件
              </button>
              <Link
                to="/"
                className="px-4 py-2 rounded-md border border-background-300 hover:border-primary-400 text-sm text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}